import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PhonebookContactAutocomplete from "../../phonebook/phonebookAutocomplete/PhonebookContactAutocomplete";
import { Contact } from "../../orderEntry/types/Contact";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (contact: Contact, isNewMarketingSource: boolean) => void;
  title: string;
  label: string;
  contactType: string;
};

const EditContactModal: React.FC<Props> = ({ open, onClose, onSave, title, label, contactType }) => {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isMarketingSource, setIsMarketingSource] = useState(false);

  // 📨 New state for CC emails
  const [ccEmails, setCcEmails] = useState<string[]>([""]);

  const handleAddCcEmail = () => {
    setCcEmails([...ccEmails, ""]);
  };

  const handleCcEmailChange = (index: number, value: string) => {
    const updated = [...ccEmails];
    updated[index] = value;
    setCcEmails(updated);
  };

  const resetModalStates = () => {
    setSelectedContact(null);
    setIsMarketingSource(false);
    setCcEmails([""]);
  };

  const resetAndModalAndHandleClose = () => {
    resetModalStates();
    onClose();
  };

  return (
      <Dialog open={open} onClose={resetAndModalAndHandleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <PhonebookContactAutocomplete
                apiUrl={`/api/clientphonebook/external/searchContactsForMomentum`}
                apiOptions={{
                  contactType: contactType === "Other" ? label : contactType,
                  isOther:
                      contactType === "Other" ||
                      contactType.includes("Principal") ||
                      contactType === "Buyer Contact" ||
                      contactType === "Seller Contact",
                }}
                contact={selectedContact}
                setContact={(contact) => {
                  if (typeof contact === "string" || !contact) {
                    setSelectedContact(null);
                  } else {
                    setSelectedContact(contact);
                  }
                }}
                textboxLabel={label}
                required={false}
                textboxPlaceholder=""
                isLimitToContactsOnly={true}
                showAddOption={false}
            />

            {/* 🧩 CC Emails Section */}
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                CC Email Addresses
              </Typography>

              {ccEmails.map((email, index) => (
                  <TextField
                      key={index}
                      variant="outlined"
                      size="small"
                      placeholder="Enter CC email"
                      value={email}
                      onChange={(e) => handleCcEmailChange(index, e.target.value)}
                  />
              ))}

              <Stack direction="row" justifyContent="flex-start">
                <IconButton
                    onClick={handleAddCcEmail}
                    size="small"
                    color="primary"
                    sx={{ mt: 0.5 }}
                >
                  <AddIcon />
                </IconButton>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, ml: 0.5 }}
                >
                  Add another
                </Typography>
              </Stack>
            </Stack>

            <FormControlLabel
                control={
                  <Checkbox
                      checked={isMarketingSource}
                      onChange={(e) => setIsMarketingSource(e.target.checked)}
                  />
                }
                label="Marketing Source"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={resetAndModalAndHandleClose}>Cancel</Button>
          <Button
              variant="contained"
              disabled={!selectedContact}
              onClick={() => {
                if (selectedContact) {
                  const isNewMarketingSource = !selectedContact.isMarketingSource && isMarketingSource;
                  const ccList = ccEmails.map((e) => e.trim()).filter((e) => e).join(";");

                  let combinedEmail = "";
                  if (selectedContact.email && ccList) {
                    combinedEmail = `${selectedContact.email};${ccList}`;
                  } else if (selectedContact.email) {
                    combinedEmail = selectedContact.email;
                  } else {
                    combinedEmail = ccList;
                  }

                  onSave(
                      { ...selectedContact, email: combinedEmail, isMarketingSource },
                      isNewMarketingSource
                  );

                  resetModalStates();
                }
              }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default EditContactModal;
